import { useState, useEffect } from 'react';
import { Dna, RefreshCw, Zap, ClipboardList, Scale, Leaf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUserIdentity } from '../../services/trainingService';
import { resetOnboarding, ProductivityArchetype } from '../../services/onboardingService';

interface ArchetypeConfig {
    name: string;
    emoji: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    description: string;
}

const ARCHETYPE_CONFIGS: Record<ProductivityArchetype, ArchetypeConfig> = {
    'chaos-thriver': {
        name: 'Chaos Thriver',
        emoji: '🔥',
        icon: <Zap className="w-6 h-6" />,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        description: 'You thrive in dynamic environments and love the energy of switching gears. Structure feels limiting - you prefer to flow with your natural rhythm.',
    },
    'structured-planner': {
        name: 'Structured Planner',
        emoji: '📋',
        icon: <ClipboardList className="w-6 h-6" />,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        description: "You're at your best with a clear plan. Knowing what's next gives you confidence, and you appreciate systems that keep you organized.",
    },
    'balanced-adapter': {
        name: 'Balanced Adapter',
        emoji: '⚖️',
        icon: <Scale className="w-6 h-6" />,
        color: 'text-teal-600',
        bgColor: 'bg-teal-100 dark:bg-teal-900/30',
        description: "You blend structure with flexibility. Some planning helps, but you're comfortable pivoting when life happens.",
    },
    'zen-minimalist': {
        name: 'Zen Minimalist',
        emoji: '🧘',
        icon: <Leaf className="w-6 h-6" />,
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        description: "Less is more for you. You focus on what truly matters and aren't afraid to let go of the rest.",
    },
};

export default function ProductivityDNACard() {
    const navigate = useNavigate();
    const [archetype, setArchetype] = useState<ProductivityArchetype | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        const identity = getUserIdentity();
        if (identity.productivityArchetype) {
            setArchetype(identity.productivityArchetype);
        }
    }, []);

    const handleRetake = () => {
        if (!showConfirm) {
            setShowConfirm(true);
            return;
        }
        resetOnboarding();
        // Navigate to onboarding flow (you may need to create this route)
        navigate('/onboarding');
    };

    // No archetype set yet
    if (!archetype) {
        return (
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Dna className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Productivity DNA</h2>
                        <p className="text-xs text-[#7B8C9B]">Discover your work style</p>
                    </div>
                </div>

                <p className="text-sm text-[#7B8C9B] mb-4">
                    Take a quick 4-question assessment to help the AI understand your natural productivity style.
                </p>

                <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full py-2.5 px-4 rounded-xl bg-[#0D5F6B] text-white font-medium text-sm"
                >
                    Take Assessment
                </button>
            </section>
        );
    }

    const config = ARCHETYPE_CONFIGS[archetype];

    return (
        <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center ${config.color}`}>
                    {config.icon}
                </div>
                <div className="flex-1">
                    <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Productivity DNA</h2>
                    <p className="text-xs text-[#7B8C9B]">Your work style profile</p>
                </div>
            </div>

            {/* Archetype Display */}
            <div className={`${config.bgColor} rounded-xl p-4 mb-4`}>
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{config.emoji}</span>
                    <h3 className={`text-lg font-bold ${config.color}`}>{config.name}</h3>
                </div>
                <p className="text-sm text-[#4A5C6A] dark:text-gray-300">
                    {config.description}
                </p>
            </div>

            {/* Retake Button */}
            <button
                onClick={handleRetake}
                className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium text-sm transition-colors ${showConfirm
                        ? 'bg-red-500 text-white'
                        : 'bg-[#F5F8FA] dark:bg-dark-elevated text-[#4A5C6A] dark:text-gray-300 hover:bg-[#E5EBF0]'
                    }`}
            >
                <RefreshCw className="w-4 h-4" />
                {showConfirm ? 'Confirm Retake?' : 'Retake Assessment'}
            </button>
            {showConfirm && (
                <p className="text-xs text-center text-[#7B8C9B] mt-2">
                    Click again to confirm. This will reset your productivity profile.
                </p>
            )}
        </section>
    );
}
